/**
 * Backend errors are RFC 9457 `application/problem+json`:
 *   { type, title, status, detail, instance, ... }
 * `detail` is the human-readable sentence — that is what we surface to staff.
 */

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;
  readonly traceId: string | null;

  constructor(status: number, problem: ProblemDetails, traceId: string | null) {
    super(problem.detail || problem.title || `Request failed (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
    this.traceId = traceId;
  }

  /** The session is gone or was never valid. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Authenticated, but the role lacks the permission. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Validation failure — the detail names the offending field. */
  get isValidation(): boolean {
    return this.status === 400 || this.status === 422;
  }
}

/** A network/CORS failure, i.e. the request never reached the backend. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Could not reach the server. Check your connection and try again.");
    this.name = "NetworkError";
    this.cause = cause;
  }
}
